import { ApiError } from '@micarahr/api-client';
import type {
  AssetCondition,
  AssetStatus,
  AuditModule,
  AuditOutcome,
  AuditSeverity,
  CompensationChangeType,
  LoanStatus,
  OffboardingReason,
  ProbationDecision,
  ReviewStatus,
  TaskOwnerKind,
  TaskStatus,
  UserRole,
} from '@micarahr/shared-types';
import type { AdminAttendanceRecord, AdminLeaveRequest } from './seed';

/**
 * Narrowing a server string to a union the frontend knows.
 *
 * Every status, kind and category arrives from the API as a plain string. The
 * frontend types them as unions, and something has to bridge the two. That
 * something used to be `as unknown as SomeWholeObject` at the bottom of each
 * mapper — a cast that silenced not only this question but every other one in
 * the object, including the payroll mapper handing decimal strings to a
 * formatter that reads `.minorUnits` and printing ₱NaN across the screen.
 *
 * So the bridge is explicit and per-field, and it checks.
 *
 * **It throws on an unknown value**, which is a deliberate choice over
 * substituting a fallback. The realistic cause is version skew — a deployed
 * API that has learned a new status the loaded frontend has not — and the two
 * options are a screen that says so, or a row that displays a status the
 * record does not have. A wrong status on a loan or a termination is acted on;
 * an error message is investigated. Only one of those is recoverable.
 *
 * The failure is loud in development too, where seeded data exercises every
 * vocabulary, which is where drift should be caught in the first place.
 */
export function narrow<T extends string>(allowed: readonly T[], value: string, field: string): T {
  const matched = matchVocabulary(allowed, value);
  if (matched !== null) return matched;

  throw new ApiError({
    code: 'UNKNOWN_SERVER_VALUE',
    status: 502,
    message: `The server sent a ${field} this version of the app does not recognise. Reload the page to pick up the latest version.`,
    category: 'unknown',
    details: { field, value, allowed },
  });
}

/**
 * A vocabulary member, forgiving spelling: "Resignation" and "end of contract"
 * are `resignation` and `end_of_contract`. Case and separators are how a
 * value typed by a person differs from the code; they never change its meaning.
 */
function matchVocabulary<T extends string>(allowed: readonly T[], value: string): T | null {
  if ((allowed as readonly string[]).includes(value)) return value as T;
  const normalised = value
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  return (allowed as readonly string[]).includes(normalised) ? (normalised as T) : null;
}

/**
 * Narrowing for a descriptive field, where an unknown value is shown rather
 * than refused.
 *
 * `narrow` throws, and on a list that fails the whole screen for one row. That
 * is right for a status somebody acts on; it is wrong for a label like a
 * separation reason, where one stray row made the offboarding list "could not
 * be loaded" for everybody. Null means "not one we know" — the caller shows
 * the server's own words, humanized.
 */
export function narrowDescriptive<T extends string>(
  allowed: readonly T[],
  value: string,
): T | null {
  return matchVocabulary(allowed, value);
}

/** The same, for a field the server may legitimately leave unset. */
export function narrowOptional<T extends string>(
  allowed: readonly T[],
  value: string | null | undefined,
  field: string,
): T | null {
  if (value === null || value === undefined || value === '') return null;
  return narrow(allowed, value, field);
}

/*
 * The vocabularies.
 *
 * Declared as `const` arrays rather than derived from the types, because a
 * TypeScript union has no runtime form to check against. Each list must match
 * its type in @micarahr/shared-types exactly; `satisfies` makes the compiler
 * enforce that a member is spelled correctly, and the exhaustiveness assertion
 * below each one makes it enforce that none is missing — so adding a status to
 * the type without adding it here fails the build rather than the page.
 */

type Exhaustive<TUnion extends string, TList extends readonly TUnion[]> =
  Exclude<TUnion, TList[number]> extends never
    ? TList
    : ['missing from the runtime list:', Exclude<TUnion, TList[number]>];

export const REQUEST_STATUSES = [
  'pending_manager',
  'pending_hr',
  'approved',
  'rejected',
  'cancelled',
  'returned',
] as const;

export const TASK_STATUSES = [
  'not_started',
  'in_progress',
  'waiting',
  'blocked',
  'completed',
  'cancelled',
] as const;

export const TASK_OWNER_KINDS = ['hr', 'manager', 'employee', 'it', 'finance', 'admin'] as const;

export const ATTENDANCE_DAY_STATUSES = [
  'not_started',
  'on_time',
  'late',
  'working',
  'on_break',
  'completed',
  'incomplete',
  'absent',
  'rest_day',
  'holiday',
  'on_leave',
  'missing_time_in',
  'missing_time_out',
  'pending_review',
  'flagged',
  'corrected',
  'rejected',
] as const;

export const PROBATION_DECISIONS = [
  'pending',
  'confirm_employment',
  'extend',
  'end_employment',
  'return_for_information',
] as const;

export const OFFBOARDING_REASONS = [
  'resignation',
  'end_of_contract',
  'termination',
  'retirement',
  'redundancy',
] as const;

export const REVIEW_STATUSES = [
  'draft',
  'employee_review',
  'manager_review',
  'calibration',
  'finalized',
  'acknowledged',
] as const;

export const ASSET_CONDITIONS = ['new', 'good', 'fair', 'poor', 'damaged'] as const;

export const ASSET_STATUSES = [
  'available',
  'assigned',
  'maintenance',
  'lost',
  'damaged',
  'retired',
] as const;

export const LOAN_STATUSES = [
  'draft',
  'pending',
  'approved',
  'active',
  'completed',
  'cancelled',
  'defaulted',
] as const;

export const COMPENSATION_CHANGE_TYPES = [
  'initial',
  'merit_increase',
  'promotion',
  'market_adjustment',
  'correction',
  'demotion',
  'one_time_adjustment',
] as const;

export const AUDIT_OUTCOMES = ['succeeded', 'failed', 'blocked', 'refused'] as const;

export const AUDIT_SEVERITIES = ['info', 'notice', 'sensitive', 'critical'] as const;

export const AUDIT_MODULES = [
  'employees',
  'organization',
  'attendance',
  'schedules',
  'timesheets',
  'leave',
  'overtime',
  'payroll',
  'compensation',
  'benefits',
  'loans',
  'recruitment',
  'onboarding',
  'probation',
  'performance',
  'learning',
  'documents',
  'policies',
  'announcements',
  'assets',
  'employee_relations',
  'offboarding',
  'users',
  'roles',
  'settings',
  'auth',
  'simulation',
  'approvals',
  'devices',
  'exports',
  'imports',
  'media',
  'platform',
  'registries',
] as const;

export const USER_ROLES = [
  'super_administrator',
  'company_administrator',
  'hr_administrator',
  'hr_officer',
  'payroll_administrator',
  'payroll_officer',
  'recruiter',
  'department_head',
  'manager',
  'team_leader',
  'employee',
  'auditor',
  'asset_custodian',
  'learning_administrator',
] as const;

/*
 * Every list above, checked against its type.
 *
 * These assignments produce no runtime code. They exist so that adding a
 * status to a union in @micarahr/shared-types and forgetting it here fails
 * `pnpm typecheck` — with the missing member named in the error — instead of
 * failing in a browser when a record with that status is first loaded.
 */
export type VocabularyChecks = [
  Exhaustive<AdminLeaveRequest['status'], typeof REQUEST_STATUSES>,
  Exhaustive<TaskStatus, typeof TASK_STATUSES>,
  Exhaustive<TaskOwnerKind, typeof TASK_OWNER_KINDS>,
  Exhaustive<AdminAttendanceRecord['status'], typeof ATTENDANCE_DAY_STATUSES>,
  Exhaustive<ProbationDecision, typeof PROBATION_DECISIONS>,
  Exhaustive<OffboardingReason, typeof OFFBOARDING_REASONS>,
  Exhaustive<ReviewStatus, typeof REVIEW_STATUSES>,
  Exhaustive<AssetCondition, typeof ASSET_CONDITIONS>,
  Exhaustive<AssetStatus, typeof ASSET_STATUSES>,
  Exhaustive<LoanStatus, typeof LOAN_STATUSES>,
  Exhaustive<CompensationChangeType, typeof COMPENSATION_CHANGE_TYPES>,
  Exhaustive<AuditOutcome, typeof AUDIT_OUTCOMES>,
  Exhaustive<AuditSeverity, typeof AUDIT_SEVERITIES>,
  Exhaustive<AuditModule, typeof AUDIT_MODULES>,
  Exhaustive<UserRole, typeof USER_ROLES>,
];
